import { generatePath, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import classNames from 'classnames';
import { t, jt } from 'ttag';

import ReturnIcon from '@svg/return.svg';
import CashIcon from '@svg/cash.svg';
import PurchasesIcon from '@svg/purchases.svg';
import CashWithBasketIcon from '@svg/cash-with-basket.svg';
import { safePaymentLanding } from '@urls';
import { Alert } from '@ui/Alert';
import { Title } from '@ui/Title';
import { Button } from '@ui/Button';

import s from './SafePaymentBenefitsBanner.sss';

export type BuyerPlacement = 'checkout';
export type SellerPlacement = 'shop-settings' | 'modal';

const getBenefits = (type: 'buyer' | 'seller', placement: BuyerPlacement | SellerPlacement) => {
  const dontPayForDelivery = (
    <b
      key="0"
      className={s.bold}
    >
      {t`не платите за доставку замовлення`}
    </b>
  );
  const getMoney = (
    <b
      key="0"
      className={s.bold}
    >
      {t`отримаєте гроші`}
    </b>
  );
  const moreOrders = (
    <b
      key="0"
      className={s.bold}
    >
      {t`більше замовлень`}
    </b>
  );
  const free = (
    <b
      key="0"
      className={s.bold}
    >
      {t`безкоштовні`}
    </b>
  );
  const pickUpOrder = (
    <b
      key="0"
      className={s.bold}
    >
      {t`заберете замовлення`}
    </b>
  );
  const notComeOrRefuse = (
    <b
      key="0"
      className={s.bold}
    >
      {t`не прийдете за посилкою або відмовитесь від товару у відділенні`}
    </b>
  );
  const compensation = (
    <b
      key="0"
      className={s.bold}
    >
      {t`компенсацію до 1000 грн`}
    </b>
  );

  const BENEFITS = {
    SELLER: {
      SHOP_SETTINGS: [
        {
          text: () => jt`Ви ${dontPayForDelivery}, якщо покупець від нього відмовився`,
          icon: ReturnIcon,
        },
        {
          text: () => jt`Ви ${getMoney} на картку, коли покупець забере замовлення`,
          icon: CashIcon,
        },
        {
          text: () => jt`Продавці з Безпечною оплатою отримують ${moreOrders}`,
          icon: PurchasesIcon,
        },
      ],
      MODAL: [
        {
          text: () => jt`Доставка та повернення замовлення для вас ${free}, якщо покупець від нього відмовився`,
          icon: ReturnIcon,
        },
        {
          text: () => jt`Ви ${getMoney} на картку, коли покупець забере замовлення`,
          icon: CashIcon,
        },
      ],
    },
    BUYER: {
      CHECKOUT: [
        {
          text: () => jt`Продавець отримує гроші, лише коли ви ${pickUpOrder}`,
          icon: CashWithBasketIcon,
        },
        {
          text: () => jt`Якщо ${notComeOrRefuse}, то гроші повернемо вам на картку (за вирахуванням вартості доставки в одну сторону)`,
          icon: CashIcon,
        },
        {
          text: () => jt`Ви можете повернути новий товар впродовж 14 днів або отримати ${compensation} у разі відмови продавця`,
          icon: ReturnIcon,
        },
      ],
    },
  };

  if (type === 'seller') {
    if (placement === 'shop-settings') {
      return BENEFITS.SELLER.SHOP_SETTINGS;
    }
    if (placement === 'modal') {
      return BENEFITS.SELLER.MODAL;
    }
  }

  if (placement === 'checkout') {
    return BENEFITS.BUYER.CHECKOUT;
  }

  return null;
};

interface Props<T> {
  className?: string,
  type: T,
  placement: T extends 'buyer' ? BuyerPlacement : SellerPlacement
}

export function SafePaymentBenefitsBanner<T extends 'buyer' | 'seller'>({
  className,
  type,
  placement,
}: Props<T>) {
  const location = useLocation();
  const benefits = getBenefits(type, placement);

  if (!benefits?.length) {
    return null;
  }

  return (
    (
      <Alert className={classNames(s.root, className)}>
        <div className={s.content}>
          <Title
            className={s.title}
            type="h6"
            bold={false}
          >
            {type === 'seller' ? t`Вмикайте Безпечну оплату ` : t`Вибирайте Безпечну оплату `}
          </Title>
          <ul className={s.list}>
            {benefits.map(({ text, icon: Icon }, i) => (
              <li
                key={i}
                className={s.item}
              >
                <Icon
                  className={s.icon}
                  width="24"
                  height="24"
                />
                <span className={s.text}>
                  {text()}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {type === 'seller' && placement === 'shop-settings' && (
        <Button
          className={s.button}
          theme="secondary"
          to={{
            pathname: generatePath(safePaymentLanding, {
              role: 'for-seller',
            }),
            search: queryString.stringify({
              next: location.pathname,
            }),
          }}
        >
          {t`Як це працює`}
        </Button>
        )}
      </Alert>
    )
  );
}
